import { Link } from "react-router-dom";
import type { MovieReview } from "@/mocks/reviews";
import type { Movie } from "@/mocks/movies";

interface EditorPicksProps {
  reviews: MovieReview[];
  movies: Movie[];
}

export default function EditorPicks({ reviews, movies }: EditorPicksProps) {
  const [lead, ...rest] = reviews;

  const findMovie = (id: string) => movies.find((m) => m.id === id);

  if (!lead) {
    return (
      <section className="w-full px-4 md:px-10 py-12 md:py-16 bg-background-100">
        <div className="max-w-[1440px] mx-auto text-center text-foreground-600">
          暂无精选影评，敬请期待
        </div>
      </section>
    );
  }

  const leadMovie = findMovie(lead.movieId);

  return (
    <section className="w-full px-4 md:px-10 py-12 md:py-16 bg-background-100">
      <div className="max-w-[1440px] mx-auto">
        <div className="flex items-end justify-between gap-3 mb-8">
          <div>
            <span className="text-xs font-label tracking-[0.2em] text-primary-500">
              EDITOR&apos;S PICKS
            </span>
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mt-1">
              编辑精选影评
            </h2>
          </div>
          <Link
            to="/special"
            className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1 cursor-pointer whitespace-nowrap"
          >
            查看全部专题
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* lead review */}
          <Link
            to={`/detail/${lead.movieId}`}
            className="group relative rounded-lg overflow-hidden min-h-[320px] md:min-h-[420px] cursor-pointer"
          >
            {leadMovie && (
              <img
                src={leadMovie.backdrop}
                alt={`${leadMovie.title} 影评配图`}
                title={`${leadMovie.title} - 精选影评`}
                className="absolute inset-0 w-full h-full object-top object-cover transition-transform duration-500 group-hover:scale-105"
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/45 to-foreground-950/10"></div>
            <div className="relative z-10 h-full flex flex-col justify-end p-6 md:p-8">
              <span className="inline-flex w-fit items-center gap-1.5 px-3 py-1 rounded-full bg-accent-500 text-foreground-950 text-xs font-medium mb-3">
                <i className="ri-award-line"></i>
                头条影评
              </span>
              <h3 className="font-heading font-bold text-xl md:text-2xl text-background-50 leading-snug">
                {lead.title}
              </h3>
              <p className="mt-3 text-sm text-background-200 leading-relaxed line-clamp-3">
                {lead.excerpt}
              </p>
              <div className="mt-4 flex items-center gap-3 text-xs text-background-300">
                <span className="flex items-center gap-1">
                  <i className="ri-user-3-line"></i>
                  {lead.author}
                </span>
                <span className="w-1 h-1 rounded-full bg-background-400"></span>
                <span>{lead.date}</span>
                <span className="w-1 h-1 rounded-full bg-background-400"></span>
                <span className="flex items-center gap-1">
                  <i className="ri-heart-3-line"></i>
                  {lead.likes}
                </span>
              </div>
            </div>
          </Link>

          {/* list */}
          <div className="flex flex-col gap-4">
            {rest.slice(0, 3).map((r) => {
              const m = findMovie(r.movieId);
              return (
                <Link
                  key={r.id}
                  to={`/detail/${r.movieId}`}
                  className="group flex gap-4 p-4 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
                >
                  {m && (
                    <div className="w-20 h-28 md:w-24 md:h-32 shrink-0 rounded-md overflow-hidden bg-background-200">
                      <img
                        src={m.poster}
                        alt={`${m.title} 海报`}
                        title={`${m.title} - 影评`}
                        className="w-full h-full object-top object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  )}
                  <div className="flex-1 min-w-0 flex flex-col">
                    <h3 className="font-heading font-semibold text-sm md:text-base text-foreground-950 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {r.title}
                    </h3>
                    <p className="mt-2 text-xs md:text-sm text-foreground-600 line-clamp-2 leading-relaxed">
                      {r.excerpt}
                    </p>
                    <div className="mt-auto pt-3 flex items-center gap-3 text-xs text-foreground-500">
                      <span className="flex items-center gap-1">
                        <i className="ri-user-3-line"></i>
                        {r.author}
                      </span>
                      <span>{r.date}</span>
                      <span className="ml-auto flex items-center gap-1 text-primary-500">
                        <i className="ri-heart-3-line"></i>
                        {r.likes}
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}